import { z } from 'zod';
import { idSchema, instantSchema } from './common.js';
/**
 * Pipeline identities (TS-1878): which pipeline — or which custom field — in an external system a
 * CRM pipeline corresponds to, following the `field_identities` pattern (TS-1840). RecruitCRM is
 * the only system today. Two kinds of link (`target_kind`):
 *
 * - `pipeline`: **Sales (a CRM project pipeline) → RecruitCRM Deals**. When a project moves to
 *   another stage of a linked pipeline, the sync moves the project's RCRM deal to the RCRM stage
 *   with the same slug (`slugifyStage` of RCRM's label — there is no per-stage table). Push by
 *   default; `both` also moves the project when its RCRM deal's stage changes in RCRM (the
 *   receiver).
 *   The external pipeline's name is a SNAPSHOT the person linking records — RCRM's
 *   `GET /v1/deals-pipeline` lists stages, not a pipeline name, so the sync cannot check it.
 * - `custom_field` (TS-1858, the user 2026-11-01): **a Candidate Flow pipeline ↔ RecruitCRM's
 *   candidate custom field 2, "Candidate Stage"**. The candidate's stage IN THAT PIPELINE is what
 *   the push writes into the field, and what the receiver moves when RCRM changes it (`both`, the
 *   default; `push` = CRM → RCRM only). RCRM has no candidate pipeline, only that field, so the
 *   link names the field by id with its name as a snapshot. No link → the field is neither written
 *   nor read: never the default pipeline by assumption.
 */
export const PIPELINE_IDENTITY_SYSTEMS = ['recruitcrm'];
export const pipelineIdentitySystemSchema = z.enum(PIPELINE_IDENTITY_SYSTEMS);
/**
 * `pipeline`: the CRM stage drives the external record's own stage. `custom_field`: the CRM stage
 * is the value of an external custom field.
 */
export const PIPELINE_IDENTITY_TARGET_KINDS = ['pipeline', 'custom_field'];
export const pipelineIdentityTargetKindSchema = z.enum(PIPELINE_IDENTITY_TARGET_KINDS);
/**
 * `push` (CRM → external) or `both` (also external → CRM, through the RecruitCRM receiver). A
 * `custom_field` link is `both` by default, a `pipeline` link `push`: receiving RCRM deal stages
 * is turned on per environment (a PATCH to `both`).
 */
export const PIPELINE_IDENTITY_DIRECTIONS = ['push', 'both'];
export const pipelineIdentityDirectionSchema = z.enum(PIPELINE_IDENTITY_DIRECTIONS);
/**
 * CRM pipeline type (`pipelines.applies_to`) → the RCRM entity it may link to, and how. A project
 * pipeline drives an RCRM deal's stage (TS-1859 seeds RCRM's deal stages into Sales); a Candidate
 * Flow pipeline is an RCRM candidate custom field. Every other type is refused: Job Flow keeps
 * `RCRM_JOB_STATUS_MAP`; Shortlist Flow and Deal Flow have no link.
 */
export const PIPELINE_IDENTITY_EXTERNAL_ENTITY = {
    project: 'deals',
    candidate: 'candidates',
};
export const PIPELINE_IDENTITY_CRM_TYPES = Object.keys(PIPELINE_IDENTITY_EXTERNAL_ENTITY);
export const pipelineIdentityCrmTypeSchema = z.enum(PIPELINE_IDENTITY_CRM_TYPES);
/** The one kind of link each linkable type takes. */
export const PIPELINE_IDENTITY_TARGET_KIND = {
    project: 'pipeline',
    candidate: 'custom_field',
};
export const PIPELINE_IDENTITY_EXTERNAL_ENTITIES = ['deals', 'candidates'];
/**
 * The RCRM custom fields a pipeline may stand for, per CRM type: candidate field 2, "Candidate
 * Stage" — the one RCRM candidate field that holds a stage (`RCRM_CUSTOM_FIELDS.candidates.stage`).
 * Another id is a 400: it would write stage names into an unrelated field.
 */
export const PIPELINE_IDENTITY_CUSTOM_FIELDS = {
    candidate: [{ id: 2, name: 'Candidate Stage' }],
};
/** The RCRM entity a CRM pipeline of `appliesTo` links to, or null when that type cannot link. */
export function pipelineIdentityExternalEntityFor(appliesTo) {
    return Object.hasOwn(PIPELINE_IDENTITY_EXTERNAL_ENTITY, appliesTo)
        ? PIPELINE_IDENTITY_EXTERNAL_ENTITY[appliesTo]
        : null;
}
const externalNameSchema = z.string().trim().min(1).max(200);
/**
 * Link a project pipeline to RCRM's deals pipeline. The external entity is derived from the
 * pipeline's type. RCRM has a single deals pipeline (`GET /v1/deals-pipeline`), so
 * `externalPipelineId` must stay null; the column exists so a second RCRM pipeline needs no
 * migration.
 */
const pipelineLinkCreateSchema = z.object({
    pipelineId: idSchema,
    system: pipelineIdentitySystemSchema.default('recruitcrm'),
    targetKind: z.literal('pipeline').default('pipeline'),
    /** Always null today: RecruitCRM has one deals pipeline. */
    externalPipelineId: z.null().optional(),
    /** The RCRM pipeline's name as the person linking sees it, e.g. `Deals` (a snapshot). */
    externalPipelineName: externalNameSchema,
    direction: pipelineIdentityDirectionSchema.default('push'),
});
/** Link a Candidate Flow pipeline to an RCRM candidate custom field (`PIPELINE_IDENTITY_CUSTOM_FIELDS`). */
const customFieldLinkCreateSchema = z.object({
    pipelineId: idSchema,
    system: pipelineIdentitySystemSchema.default('recruitcrm'),
    targetKind: z.literal('custom_field'),
    externalFieldId: z.number().int().positive(),
    /** The RCRM field's name as the person linking sees it, e.g. `Candidate Stage` (a snapshot). */
    externalFieldName: externalNameSchema,
    direction: pipelineIdentityDirectionSchema.default('both'),
});
export const pipelineIdentityCreateSchema = z.union([
    pipelineLinkCreateSchema,
    customFieldLinkCreateSchema,
]);
/**
 * Refresh a name snapshot, or turn receiving on or off. Nothing else
 * changes: to link another CRM pipeline or field, delete the link and create one.
 */
export const pipelineIdentityPatchSchema = z
    .object({
    externalPipelineName: externalNameSchema.optional(),
    externalFieldName: externalNameSchema.optional(),
    direction: pipelineIdentityDirectionSchema.optional(),
})
    .refine((p) => Object.values(p).some((v) => v !== undefined), {
    message: 'Nothing to change',
});
export const pipelineIdentityQuerySchema = z.object({
    pipelineId: idSchema.optional(),
    crmType: pipelineIdentityCrmTypeSchema.optional(),
});
/** A link as the API returns it. */
export const pipelineIdentitySchema = z.object({
    id: idSchema,
    system: pipelineIdentitySystemSchema,
    pipelineId: idSchema,
    /** The linked CRM pipeline (pipelines are archived, never deleted). */
    pipeline: z.object({ name: z.string(), archivedAt: instantSchema.nullable() }),
    /** The CRM pipeline's type (`applies_to`). */
    crmType: pipelineIdentityCrmTypeSchema,
    targetKind: pipelineIdentityTargetKindSchema,
    externalEntity: z.enum(PIPELINE_IDENTITY_EXTERNAL_ENTITIES),
    externalPipelineId: z.string().nullable(),
    /** A `pipeline` link's RCRM pipeline name; null on a `custom_field` link. */
    externalPipelineName: z.string().nullable(),
    /** A `custom_field` link's RCRM field id and name snapshot; null on a `pipeline` link. */
    externalFieldId: z.number().int().nullable(),
    externalFieldName: z.string().nullable(),
    direction: pipelineIdentityDirectionSchema,
    createdAt: instantSchema,
    updatedAt: instantSchema,
});
//# sourceMappingURL=pipeline-identities.js.map